import { Pagination } from "@/components/Util/Table/pagination";
import { getTablePageData } from "@/utils/Utils";

import Table from "./Table";
import TableV2 from "./TableV2";
import { cn } from "@/lib/utils";
import { ComponentProps } from "react";

type TableOption = "Table" | "Tablev2";

type TableProps<TO extends TableOption> = TO extends "Table"
  ? ComponentProps<typeof Table>
  : TO extends "Tablev2"
    ? ComponentProps<typeof TableV2>
    : never;

type Props<
  TO extends TableOption,
  TE extends TableProps<TO>,
  Item extends object,
> = Omit<TE, "tableLines"> &
  Omit<ComponentProps<typeof Pagination>, "items"> & {
    table: TO;
    items: Item[];
    maxItemCount?: number;
    createTableRow: (item: Item, index: number) => React.ReactNode;
    className?: string;
    alwaysShowPagination?: true;
  };

export default function PaginatedTable<
  TO extends TableOption,
  TE extends TableProps<TO>,
  Item extends object,
>({
  table,
  className,
  items,
  maxItemCount,
  alwaysShowPagination,
  createTableRow,
  ...props
}: Props<TO, TE, Item>) {
  function createTableLines() {
    return (
      maxItemCount === undefined
        ? getTablePageData(props.currentPage, props.itemsPerPage, items)
        : items
    ).map((item: Item, index: number) => createTableRow(item, index));
  }

  return items.length > 0 ? (
    <div className={cn("paginated-table", className)}>
      {table === "Tablev2" ? (
        <TableV2 {...props} tableLines={createTableLines()} />
      ) : (
        <Table {...props} tableLines={createTableLines()} />
      )}
      {(items.length > props.itemsPerPage || alwaysShowPagination) && (
        <Pagination {...props} items={maxItemCount ?? items.length} />
      )}
    </div>
  ) : (
    <></>
  );
}
